import { useEffect, useRef, useState } from "react";
import {
  Bot,
  Loader2,
  Mic,
  Send,
  Square,
  Trash2,
  User,
  Volume2,
  VolumeX,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { agentsApi, voiceApi, type ToolCall } from "@/features/agents/api";
import { cn } from "@/lib/utils";
import ToolCallBlock from "./ToolCallBlock";

type Message =
  | { role: "user"; content: string }
  | { role: "assistant"; content: string }
  | { role: "tools"; calls: ToolCall[] };

export default function AgentChatTab({
  agentId,
  voiceProvider,
  voiceId,
  ttsModel,
}: {
  agentId: string;
  voiceProvider?: string;
  voiceId?: string;
  ttsModel?: string;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  // STT state
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // TTS state
  const [playingIdx, setPlayingIdx] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    },
    [],
  );

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || pending) return;
    setInput("");
    setError("");
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setPending(true);
    try {
      const data = await agentsApi.chat(agentId, {
        message: trimmed,
        conversation_id: conversationId ?? undefined,
      });
      if (data.conversation_id) setConversationId(data.conversation_id);
      const reply = data.response ?? data.message ?? "";
      const calls = data.tool_calls ?? [];
      setMessages((m) => {
        const next = [...m];
        // Surface any tool calls inline, just before the agent's reply.
        if (calls.length) next.push({ role: "tools", calls });
        next.push({ role: "assistant", content: reply });
        return next;
      });
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err?.response?.data?.error || "Failed to get response.");
    } finally {
      setPending(false);
    }
  }

  async function clearChat() {
    if (conversationId) {
      try {
        await agentsApi.clearChat(agentId, conversationId);
      } catch {
        // ignore — clearing local state is what matters
      }
    }
    setMessages([]);
    setConversationId(null);
    setError("");
    stopAudio();
  }

  // ── STT (mic → text → send) ────────────────────────────────────────────
  async function startRecording() {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        chunksRef.current = [];
        if (blob.size === 0) return;
        setTranscribing(true);
        try {
          const r = await voiceApi.transcribe(blob);
          if (r.text?.trim()) {
            await send(r.text);
          } else {
            toast.info("Couldn't hear that — try again.");
          }
        } catch {
          toast.error("Transcription failed");
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      const err = e as { name?: string; message?: string };
      if (err?.name === "NotAllowedError") {
        setError("Microphone permission denied.");
      } else {
        setError(err?.message || "Failed to start recording.");
      }
    }
  }

  function stopRecording() {
    setRecording(false);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
  }

  // ── TTS (synthesize + play) ────────────────────────────────────────────
  function stopAudio() {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingIdx(null);
  }

  async function speak(idx: number, text: string) {
    if (playingIdx === idx) {
      stopAudio();
      return;
    }
    stopAudio();
    try {
      const r = await voiceApi.synthesize({
        text,
        voice_id: voiceId,
        tts_provider: voiceProvider,
        tts_model: ttsModel,
      });
      const bytes = Uint8Array.from(atob(r.audio_base64), (c) =>
        c.charCodeAt(0),
      );
      const blob = new Blob([bytes], {
        type: r.content_type || "audio/mpeg",
      });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      setPlayingIdx(idx);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (audioRef.current === audio) {
          audioRef.current = null;
          setPlayingIdx(null);
        }
      };
      await audio.play();
    } catch {
      setPlayingIdx(null);
      toast.error("TTS failed. Check provider keys.");
    }
  }

  const sttBusy = recording || transcribing;

  return (
    <div className="flex h-[600px] flex-col rounded-lg border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <span className="text-sm font-medium">Test conversation</span>
        {messages.length > 0 && (
          <Button variant="ghost" size="sm" onClick={clearChat}>
            <Trash2 className="size-4" />
            Clear
          </Button>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
            <Bot className="size-6" />
            <p>
              Type or speak a message to test how the agent responds. Click the
              speaker on a reply to hear it.
            </p>
          </div>
        )}
        {messages.map((m, i) =>
          m.role === "tools" ? (
            <div key={i} className="space-y-1.5 pl-9 pr-2">
              {m.calls.map((c, j) => (
                <ToolCallBlock key={j} call={c} />
              ))}
            </div>
          ) : (
          <div
            key={i}
            className={cn(
              "flex gap-2.5",
              m.role === "user" && "flex-row-reverse",
            )}
          >
            <div
              className={cn(
                "flex size-7 shrink-0 items-center justify-center rounded-full",
                m.role === "assistant"
                  ? "bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {m.role === "assistant" ? (
                <Bot className="size-4" />
              ) : (
                <User className="size-4" />
              )}
            </div>
            <div
              className={cn(
                "group relative max-w-[80%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm",
                m.role === "assistant"
                  ? "bg-muted"
                  : "bg-primary text-primary-foreground",
              )}
            >
              {m.content}
              {m.role === "assistant" && (
                <button
                  type="button"
                  onClick={() => speak(i, m.content)}
                  className={cn(
                    "absolute -right-9 top-1/2 -translate-y-1/2 inline-flex size-7 items-center justify-center rounded-full border bg-background text-muted-foreground transition-opacity",
                    playingIdx === i
                      ? "opacity-100 text-primary"
                      : "opacity-0 group-hover:opacity-100",
                  )}
                  aria-label={
                    playingIdx === i ? "Stop playback" : "Play with voice"
                  }
                >
                  {playingIdx === i ? (
                    <VolumeX className="size-3.5" />
                  ) : (
                    <Volume2 className="size-3.5" />
                  )}
                </button>
              )}
            </div>
          </div>
          ),
        )}
        {pending && (
          <div className="flex gap-2.5">
            <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
              <Bot className="size-4" />
            </div>
            <div className="inline-flex items-center gap-1 rounded-lg bg-muted px-3 py-2.5">
              <Dot delay={0} />
              <Dot delay={150} />
              <Dot delay={300} />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {error && (
        <div className="border-t border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-end gap-2 border-t p-3">
        <Button
          type="button"
          variant={recording ? "destructive" : "outline"}
          size="icon"
          onClick={recording ? stopRecording : startRecording}
          disabled={transcribing || pending}
          aria-label={recording ? "Stop recording" : "Record voice"}
          className="shrink-0"
        >
          {transcribing ? (
            <Loader2 className="size-4 animate-spin" />
          ) : recording ? (
            <Square className="size-4 fill-current" />
          ) : (
            <Mic className="size-4" />
          )}
        </Button>
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          placeholder={
            recording
              ? "Recording…"
              : transcribing
              ? "Transcribing…"
              : "Type a message or click the mic…"
          }
          rows={1}
          disabled={sttBusy}
          className="min-h-9 resize-none"
        />
        <Button
          onClick={() => send(input)}
          disabled={!input.trim() || pending || sttBusy}
        >
          <Send className="size-4" />
          Send
        </Button>
      </div>
    </div>
  );
}

function Dot({ delay }: { delay: number }) {
  return (
    <span
      className="inline-block size-1.5 animate-bounce rounded-full bg-muted-foreground/60"
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}
